export type LabelVariant = 'rectangle' | 'pill' | 'tag' | 'outline' | 'plaque' | 'neon';

export interface LabelStyle {
  fontSize?: number;
  fontWeight?: 'normal' | 'bold';
  color?: string;
  backgroundColor?: string;
  backgroundOpacity?: number;
  borderColor?: string;
  padding?: number;
  zIndex?: number;
  variant?: LabelVariant;
  // Slant in degrees (rhomboid shape, for perspective)
  skewX?: number;
  skewY?: number;
  // Perspective in degrees: turned around its vertical centre line (X), and
  // leaning back around its bottom edge (Y)
  perspectiveX?: number;
  perspectiveY?: number;
}

export const DEFAULT_LABEL_STYLE: Required<Pick<LabelStyle, 'fontSize' | 'fontWeight' | 'color' | 'backgroundColor' | 'backgroundOpacity' | 'borderColor' | 'padding' | 'zIndex'>> = {
  fontSize: 13,
  fontWeight: 'bold',
  color: '#ffffff',
  backgroundColor: '#333333',
  backgroundOpacity: 0.8,
  borderColor: 'transparent',
  padding: 6,
  zIndex: 400,
};

export interface LabelType {
  id: string;
  name: string;
  preferences?: LabelPreferences;
}

export interface LabelPreferences {
  style?: LabelStyle;
}

export interface LabelSelectionGroup {
  name: string;
  labelIds: string[];
  color?: string;
}

export interface LabelPositionType {
  x: number;
  y: number;
  rotation?: number;
}

/** Label style of an overlay: drives its polygon look and its label badge */
export type OverlayStyle = LabelStyle & {
  // Zoom showing the label at its size, scaling it with the map at other zooms
  scaleZoom?: number;
  // Label always shown, instead of only while the overlay is hovered
  alwaysShowLabel?: boolean;
};

/** Polygon drawn over an image layer, optionally showing a label and/or representing a space */
export interface OverlayType {
  id: string;
  points: { x: number; y: number }[];
  labelId: string | null;
  spaceId: string | null;
  // Layer opened when clicked, instead of a space
  layerId?: number | null;
  style?: OverlayStyle | null;
}

export const DEFAULT_LABEL_GROUP_COLOR = '#1f3b5f';

export interface FileType {
  url: string;
  thumb: string;
  base64?: string; // new file as base64
  filename?: string; // new file filename
}

export interface ImageType extends FileType {
  id: string;
  position_order: number;
}

export interface SpaceType {
  id: string;
  model: number;
  vr_model: number;
  vr_iframe: string;
  name: string;
  subtitle: string;
  description: string;
  external_link: string;
  // Space can have 3 types of visual (image for sidebar and popup):
  visual_id: number;          // id, from model visuals
  visual_url: string;         // external URL
  visual_uploaded: ImageType; // uploaded to platform
  visual?: string; // Set on spaces store from visual_id, visual_url or visual_uploaded
  video: string;
  pdf: FileType;
  category: string;
  category_id: string;
  position_order: number;
  carousel_images: ImageType[];
  pageFooter: string;
  auto_zoom: boolean;
  isWaypoint?: boolean; // Mark space as waypoint for routing
  markerId?: MarkerType['id'];
}

export interface SpacePositionType {
  x: number;
  y: number;
  layerId: number;
}

export interface CategoryType {
  id: string;
  name: string;
  icon: string;
  position_order: number;
  opened: boolean;
}

export interface BaseLayerType {
  id: number;
  type: string;
  mainLayer: null | number;
  duplicateOf: number;
  name: string;
  tiles: string;
  labels: {
    id: string;
    label: string;
    position: LabelPositionType;
  }[];
  spaces: {
    id: string;
    layerId: number;
    space: string;
    position: {
      x: number;
      y: number;
    };
  }[];
  overlays?: OverlayType[];
  position_order: number;
  checked: boolean;
  disabled: boolean;
  savedView: string | null;
  opened: boolean;
}

export interface ImageLayerType extends BaseLayerType {
  fullImage: {
    original: string;
    thumb: string;
  };
  width: number;
  height: number;
  savedView: null;
}

export interface ExternalLayerType extends BaseLayerType {
  savedView: string;
  mainLayer: null;
  provider: string;
}

export type LayerType = ImageLayerType | ExternalLayerType;

export interface MarkerType {
  id: string;
  url: string;
  base64: string;
  size: string;
  decorationId?: MarkerDecorationType['id'];
  state?: string;
};

export interface MarkerDecorationType {
  id: string;
  base64: string;
}

export interface InteractiveMapType {
  id: number;
  order: {
    id: number;
    permalink: string;
    description: string;
  };
  markerId: string;
  markers: MarkerType[];
  defaultMarkers: MarkerType;
  markerDecorations: MarkerDecorationType[];
  default_image: number;
  name: string;
  featured_video: string;
  featured_image: ImageType | null;
  groupBy: string;
  allowLabels: boolean;
  categories: CategoryType[];
  spaces: SpaceType[];
  labels: LabelType[];
  images: LayerType[];
  models: ModelType[];
  current_user: {
    id: number | null;
    can_edit: boolean;
  };
  customCss: string;
  primaryColor: string;
  secondaryColor: string;
  bgColor: string;
  coverImage: string;
  paths?: PathNetwork; // Indoor navigation path network
  preferences?: PreferencesType;
  updatedAt?: string;
}

export interface VisualType {
  id: number;
  name: string;
  thumbnail: string;
}

export interface ModelType {
  id: number;
  type: string;
  name: string;
  thumbnail: string;
  visuals: VisualType[];
  vr: VrType;
  current_user: {
    id: number | null;
    can_edit: boolean;
  };
}

export interface VrType {
  id: number;
  model_id: number;
  order_id: string;
  order_description: string;
  link_icon: string;
  show_link_labels: boolean;
  primaryColor: string;
  secondaryColor: string;
  bgColor: string;
  name: string;
  prefix: string;
  path: string;
  default_view: {
    id: number;
    name: string;
    layout: string;
    light: string;
    size: string;
  };
  cubemaps: [
    {
      images: Cubemap;
      links: {
        [targetViewID: number]: Link;
      };
      title: string;
      id: number;
      name: string;
      layout: string;
      light: string;
      size: string;
    }
  ];
  dupCubemapsIds: number[];
  thumbnail: string;
  vrMenuOpen: boolean;
}

export interface Cubemap {
  posX: string;
  negX: string;
  posY: string;
  negY: string;
  posZ: string;
  negZ: string;
}

export interface Link {
  name: string;
  x: number;
  y: number;
  z: number;
}

// Indoor pathfinding types
export interface PathNode {
  id: string;
  name?: string; // Optional human-readable name for the waypoint (e.g., "Near Elevator B")
  spaceId?: string; // Optional - only set for nodes linked to a space/marker
  overlayId?: string; // Optional - only set for nodes linked to an overlay without space
  labelId?: string; // Optional - only set for nodes linked to a label waypoint
  position: { x: number; y: number };
  layerId: number;
}

export interface PathEdge {
  id: string;
  from: string; // node id
  to: string; // node id
  bidirectional: boolean;
  weight?: number; // Optional custom weight; if not provided, calculated from distance
}

export interface PathNetwork {
  nodes: PathNode[];
  edges: PathEdge[];
}

/**
 * UI preferences for the interactive map.
 * These are admin-configurable settings that control the user experience.
 */
export interface PreferencesType {
  /** Map colors, override Order and Brand colors */
  primaryColor?: string;
  secondaryColor?: string;
  bgColor?: string;
  /** Whether the sidebar is visible to end-users */
  sidebarEnabled: boolean;
  /** Position of the sidebar */
  sidebarPosition: 'left' | 'right';
  /** Whether the sidebar starts open on desktop */
  sidebarOpenOnLoad: boolean;
  /** Whether the sidebar starts open on mobile/small screens */
  sidebarOpenOnLoadMobile: boolean;
  /** What items should be opened in sidebar on load */
  sidebarOpenItems?: 'all' | 'first' | 'none';
  /** Style of marker popups: 'popup' (leaflet default), 'card' (elegant overlay) */
  popupStyle: 'popup' | 'card';
  /** Whether to show the layer/floor switcher */
  showLayerSwitcher: boolean;
  /** Whether to show zoom controls */
  showZoomControls: boolean;
  /** Position of zoom controls */
  zoomControlPosition: 'topleft' | 'topright' | 'bottomleft' | 'bottomright';
  /** Enable dark mode theme */
  darkMode: boolean;
  /** Enable compact mode (reduced spacing) */
  compactMode: boolean;
  /** Fill viewport on initial load (cover fit instead of contain) */
  fillViewport: boolean;
  /** Show waypoint nodes in edit mode */
  showWaypoints: boolean;
  /** Show wayfinding button in the top-right corner */
  wayfindingButton: boolean;
  /** Show wayfinding dropdowns in Space Card popups */
  wayfindingDropdowns: boolean;
  /** Saved weather coordinates for the weather sidebar */
  weatherCoords?: string;
  /** Default style applied to all labels unless overridden per-label */
  defaultLabelStyle?: LabelStyle;
  /** Default label rotation applied to all labels unless overridden per-label */
  defaultLabelRotation?: number;
  /** Saved named label selections for reapplying styles later */
  labelSelectionGroups?: LabelSelectionGroup[];
}

/** Default preferences */
export const DEFAULT_PREFERENCES: PreferencesType = {
  sidebarEnabled: true,
  sidebarPosition: 'left',
  sidebarOpenOnLoad: true,
  sidebarOpenOnLoadMobile: false,
  sidebarOpenItems: 'first',
  popupStyle: 'popup',
  showLayerSwitcher: true,
  showZoomControls: true,
  zoomControlPosition: 'bottomright',
  darkMode: false,
  compactMode: false,
  fillViewport: false,
  showWaypoints: true,
  wayfindingButton: true,
  wayfindingDropdowns: true,
  weatherCoords: '',
};
